/**
 * "Похожие идеи" inside the idea sheet (FEAT-022, addendum §3).
 *
 * Two things live in the idea pool: duplicates still open, and thoughts already
 * turned down with a recorded reason. The second is the valuable half — a
 * rejection is a decision, and the reviewer about to approve this idea should
 * see that we said no to the same thing in March, and why.
 *
 * The reason is quoted verbatim, never paraphrased or softened. It is
 * information for the human deciding today, not a veto: circumstances change,
 * and the person on this screen is the one who knows whether they have.
 *
 * Renders nothing at all when there is no match — an empty "похожего нет" panel
 * in a sheet is pure noise.
 */

import { useQuery } from "@tanstack/react-query";
import { Lightbulb } from "lucide-react";

import { Search } from "@/api/resources";
import type { IdeaSimilarityCard } from "@/api/types";
import { IdeaStatusBadge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { CollapsibleSection } from "@/components/ui/Section";
import { formatDayMonth, parseDate } from "@/lib/format";

export interface SimilarIdeasProps {
  /** The idea being viewed, excluded from its own results. */
  ideaId: string;
  /** Text to match on — the summary, falling back to the raw capture. */
  query: string;
}

export function SimilarIdeas({ ideaId, query }: SimilarIdeasProps) {
  const q = query.trim().slice(0, 400);
  const similar = useQuery({
    queryKey: ["similar-ideas", q],
    queryFn: () => Search.similarIdeas(q, { scope: "all", limit: 4 }),
    enabled: q.length > 0,
    staleTime: 5 * 60_000,
  });

  const cards = (similar.data ?? []).filter((c) => c.idea_id !== ideaId).slice(0, 3);

  if (similar.isLoading || similar.isError || cards.length === 0) return null;

  return (
    <CollapsibleSection
      title="Похожие идеи"
      count={cards.length}
      icon={<Lightbulb className="h-5 w-5 shrink-0 text-hint" aria-hidden="true" />}
    >
      {cards.map((card) => (
        <SimilarIdeaCard key={card.idea_id} card={card} />
      ))}
    </CollapsibleSection>
  );
}

function SimilarIdeaCard({ card }: { card: IdeaSimilarityCard }) {
  const created = parseDate(card.created_at);
  const rejected = card.status === "rejected";
  return (
    <Card className="flex flex-col gap-1.5">
      <div className="flex items-start justify-between gap-2">
        <p className="clamp-2 text-cell-title font-medium text-text">
          {card.summary ?? "Без описания"}
        </p>
        <IdeaStatusBadge status={card.status} />
      </div>

      {/*
        The reason exists only on rejected ideas — the API withholds a
        reviewer's half-formed notes on an idea still under review, and the UI
        must not invent one for any other status.
      */}
      {rejected && card.review_comment_md && (
        <p className="whitespace-pre-wrap text-body text-hint">
          <span className="text-dstr">Причина отказа: </span>
          {card.review_comment_md}
        </p>
      )}

      {created && <span className="text-chip text-hint">{formatDayMonth(created)}</span>}
    </Card>
  );
}
